import { SessionService } from '../../../../services/session/session.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import { UserVendorProductsService } from '../../../../services/uservendorproducts/uservendorproducts.service';
import { UiAlertService } from '../../../../services/ui-alert/ui-alert.service';
import { UsersInsuranceService } from '../../../../services/usersinsurance/users.insurance.service';

type RxValetSubscriptionOperation =
  | 'adddependent'
  | 'updatedependent'
  | 'deactivatedependent'
  | 'deactivateprimary'
  | 'changeplan'
  | 'updatemember';

@Component({
    selector: 'app-vendor-products-user-products',
    templateUrl: './vendor-products-user-products.component.html',
    styleUrls: ['./vendor-products-user-products.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorProductsUserProductsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  public items: any[] = [];
  public tableDataSource = new MatTableDataSource<any>();
  public columnNames = [
    'createdAt',
    'userName',
    'vendorName',
    'vendorSession',
    'wallitSession',
    'phase',
    'metadata',
    'actions',
  ];
  public searchValue = '';
  public page = 0;
  public limit = 25;
  public endOfList = false;
  public loading = false;
  public reinstating = false;
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;

  constructor(
    private userVendorProductsService: UserVendorProductsService,
    private usersInsuranceService: UsersInsuranceService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
    public sessionService: SessionService,
  ) {
    this.tableDataSource.sortingDataAccessor = (item, property) => this.getSortValue(item, property);
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items = [];
      this.tableDataSource.data = this.items;
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const hasSearch = !!this.searchValue.trim();
      const result = await this.userVendorProductsService.getUserVendorProducts(
        this.searchValue,
        hasSearch ? undefined : this.page,
        hasSearch ? undefined : this.limit,
      );
      if (!result || result.length === 0) {
        this.endOfList = true;
      } else {
        this.items = hasSearch ? result : this.items.concat(result);
        this.tableDataSource.data = this.items;
        if (hasSearch || result.length < this.limit) {
          this.endOfList = true;
        } else {
          this.page += 1;
        }
      }
    } catch (error) {
      console.error('Error loading user vendor products', error);
    } finally {
      this.loading = false;
    }
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  clearSearch(): void {
    this.searchValue = '';
    this.queueFilter('');
  }

  editItem(item: any): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: new MemberAddeditDialogModel(
        this.getUserVendorProductEditData(item),
        this.getUserVendorProductEditFields(),
        'User Vendor Product',
      ),
    });
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (!result) {
        return;
      }

      try {
        await this.userVendorProductsService.putUserVendorProduct({
          ...item,
          ...result,
        });
        this.snackBar.open('User vendor product updated', 'Close', { duration: 2500 });
        this.loadItems(true);
      } catch (error) {
        console.error('Error updating user vendor product', error);
        this.snackBar.open('Unable to update user vendor product', 'Close', { duration: 3000 });
      }
    });
  }

  async openRxValetSubscriptionUpdateDialog(item: any): Promise<void> {
    if (!item?.userId) {
      this.snackBar.open('This user vendor product is missing a userId.', 'Close', { duration: 2500 });
      return;
    }

    let dependents: any[] = [];
    try {
      const result = await this.usersInsuranceService.getInsuranceDependents(item.userId);
      dependents = Array.isArray(result) ? result.filter((dependent) => !!dependent?.id) : [];
    } catch (error) {
      console.error('Error loading insurance dependents', error);
      this.snackBar.open('Unable to load dependents for this user.', 'Close', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        { operation: '', dependentId: '', groupId: '' },
        this.getRxValetSubscriptionUpdateFields(dependents),
        'RxValet Subscription',
        true,
      ),
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {
      const operation = result?.operation as RxValetSubscriptionOperation | undefined;
      if (!operation) {
        return;
      }

      const dependentId = result?.dependentId;
      const groupId = (result?.groupId || '').toString().trim();

      if (this.rxValetOperationNeedsDependent(operation)) {
        if (!dependentId) {
          this.snackBar.open('Please select a dependent for this operation.', 'Close', { duration: 3000 });
          return;
        }
        if (!dependents.some((dep) => dep.id === dependentId)) {
          this.snackBar.open('Selected dependent was not found.', 'Close', { duration: 2500 });
          return;
        }
      }

      if (operation === 'changeplan' && !groupId) {
        this.snackBar.open('groupId is required for Change Plan.', 'Close', { duration: 3000 });
        return;
      }

      try {
        await this.runRxValetSubscriptionOperation(item, dependents, operation, dependentId, groupId);
        this.snackBar.open(`${this.getRxValetOperationLabel(operation)} update sent to vendor adapter.`, 'Close', {
          duration: 2500,
        });
        this.loadItems(true);
      } catch (error: any) {
        this.snackBar.open(this.getVendorUpdateErrorMessage(error, operation), 'Close', { duration: 4000 });
      }
    });
  }

  async reinstate(item: any): Promise<void> {
    if (this.reinstating || !this.sessionService.isSuperAdmin() || item?.metadata?.phase !== 'terminated') {
      return;
    }
    this.reinstating = true;
    try {
      const reasonDialog = this.dialog.open(MemberAddeditComponent, {
        width: '600px',
        maxWidth: '95vw',
        data: new MemberAddeditDialogModel(
          { reason: '' },
          [{ name: 'reason', label: 'Reason for reinstatement', fieldType: FieldType.F_STRING, required: true }],
          'Re-Instate',
        ),
      });
      const details = await reasonDialog.afterClosed().toPromise();
      const reason = (details?.reason || '').trim();
      if (!reason) return;
      const confirmed = await this.uiAlertService.presentAlertConfirm(
        'Restart billing today using the previous recurring price and quantity and the member’s default card? '
        + 'The first payment is due immediately. The termination will only be removed after payment succeeds '
        + 'and only if it has not been sent or selected for carrier delivery. Any old unpaid invoice remains separate.',
      );
      if (!confirmed) return;
      await this.userVendorProductsService.reinstateUserVendorProduct(item, reason);
      this.snackBar.open('Subscription restarted and pending termination removed.', 'Close', { duration: 5000 });
      await this.loadItems(true);
    } catch (error: any) {
      this.snackBar.open(
        error?.data?.error || error?.error?.error || 'Reinstatement could not be confirmed. Refresh and review this record before retrying.',
        'Close', { duration: 10000 },
      );
    } finally {
      this.reinstating = false;
    }
  }

  async terminate(item: any): Promise<void> {
    const confirm = await this.uiAlertService.presentAlertConfirm(
      'Terminate this user vendor product? This will cancel the Stripe subscription and send the vendor termination.',
    );
    if (!confirm) {
      return;
    }
    try {
      await this.userVendorProductsService.terminateUserVendorProduct(item);
      this.snackBar.open(`User vendor product ${item.productName} terminated and Stripe subscription canceled`, 'Close', {
        duration: 2500,
      });
    } catch (error: any) {
      const payrollProtected = error?.data?.code === 'PAYROLL_PRODUCT_CANCELLATION_REQUIRES_ORCHESTRATION'
        || error?.error?.code === 'PAYROLL_PRODUCT_CANCELLATION_REQUIRES_ORCHESTRATION';
      this.snackBar.open(
        payrollProtected
          ? 'Payroll deduction products must be canceled through the protected payroll workflow.'
          : 'Unable to terminate user vendor product.',
        'Close',
        { duration: 4000 },
      );
    }
  }

  async forceTerminate(item: any): Promise<void> {
    const confirm = await this.uiAlertService.presentAlertConfirm(
      'Force terminate this user vendor product? This will only mark this record as terminated and will not contact the vendor or cancel the Stripe subscription.',
    );
    if (!confirm) {
      return;
    }

    const terminatedRecord = {
      id: item.id,
      vendorSession: item.vendorSession,
      wallitSession: item.wallitSession,
      metadata: {
        ...item.metadata,
        phase: 'terminated',
      },
    };

    try {
      await this.userVendorProductsService.putUserVendorProduct(terminatedRecord);
      this.snackBar.open('User vendor product force terminated', 'Close', { duration: 2500 });
      await this.loadItems(true);
    } catch (error) {
      console.error('Error force terminating user vendor product', error);
      this.snackBar.open('Unable to force terminate user vendor product', 'Close', { duration: 3000 });
    }
  }

  async delete(item: any): Promise<void> {
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this user vendor product?');
    if (!confirm) {
      return;
    }
    await this.userVendorProductsService.deleteUserVendorProduct(item);
    this.snackBar.open(`User vendor product ${item.productName} deleted`, 'Close', { duration: 2000 });
    this.loadItems(true);
  }

  onScroll(event: any): void {
    if (this.searchValue.trim()) {
      return;
    }
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    if (scrollTop + offsetHeight >= scrollHeight - 150) {
      this.loadItems();
    }
  }

  getMetadata(item: any): string {
    if (!item?.metadata) {
      return '';
    }
    const text = JSON.stringify(item.metadata);
    return text.length > 250 ? `${text.slice(0, 247)}...` : text;
  }

  private getSortValue(item: any, property: string): string | number {
    switch (property) {
      case 'createdAt':
        return item?.createdAt ? new Date(item.createdAt).getTime() : 0;
      case 'phase':
        return (item?.metadata?.phase || '').toString().toLowerCase();
      case 'metadata':
        return this.getMetadata(item).toLowerCase();
      default:
        return (item?.[property] || '').toString().toLowerCase();
    }
  }

  supportsRxValetDependents(item: any): boolean {
    const vendorSlug = (item?.vendorSlug || '').toString().toLowerCase();
    const vendorName = (item?.vendorName || '').toString().toLowerCase();
    return vendorSlug === 'rxvalet' || vendorName.includes('rxvalet');
  }

  hasVendorActions(item: any): boolean {
    return this.supportsRxValetDependents(item);
  }

  private getRxValetSubscriptionUpdateFields(dependents: any[]): Field[] {
    return [
      {
        name: 'operation',
        label: 'Operation',
        fieldType: FieldType.F_DROPDOWN,
        required: true,
        values: this.getRxValetOperationOptions(dependents),
      },
      {
        name: 'dependentId',
        label: 'Dependent',
        fieldType: FieldType.F_DROPDOWN,
        values: dependents.map((dependent) => `${this.getDependentLabel(dependent)}:${dependent.id}`),
        invisible: (form: any) => !this.rxValetOperationNeedsDependent(form?.get('operation')?.value),
      },
      {
        name: 'groupId',
        label: 'Group ID',
        fieldType: FieldType.F_STRING,
        placeholder: 'Required for Change Plan',
        invisible: (form: any) => form?.get('operation')?.value !== 'changeplan',
      },
    ];
  }

  private getRxValetOperationOptions(dependents: any[]): string[] {
    const values: Array<[string, RxValetSubscriptionOperation]> = [
      ['Update Subscription (Primary Member)', 'updatemember'],
      ['Deactivate Primary', 'deactivateprimary'],
      ['Change Plan', 'changeplan'],
    ];

    if (dependents.length > 0) {
      values.push(
        ['Add Dependent', 'adddependent'],
        ['Update Dependent', 'updatedependent'],
        ['Deactivate Dependent', 'deactivatedependent'],
      );
    }

    return values.map(([label, value]) => `${label}:${value}`);
  }

  private rxValetOperationNeedsDependent(operation?: string): boolean {
    return ['adddependent', 'updatedependent', 'deactivatedependent'].includes((operation || '').toLowerCase());
  }

  private getRxValetOperationLabel(operation: RxValetSubscriptionOperation): string {
    switch (operation) {
      case 'adddependent':
        return 'Add Dependent';
      case 'updatedependent':
        return 'Update Dependent';
      case 'deactivatedependent':
        return 'Deactivate Dependent';
      case 'deactivateprimary':
        return 'Deactivate Primary';
      case 'changeplan':
        return 'Change Plan';
      case 'updatemember':
        return 'Update Subscription';
      default:
        return 'Subscription';
    }
  }

  private async runRxValetSubscriptionOperation(
    item: any,
    dependents: any[],
    operation: RxValetSubscriptionOperation,
    dependentId?: string,
    groupId?: string,
  ): Promise<void> {
    const dependent = dependentId ? dependents.find((dep) => dep.id === dependentId) : undefined;

    switch (operation) {
      case 'adddependent':
        if (!dependent) throw new Error('Dependent not found');
        await this.userVendorProductsService.addDependentToUserVendorProduct(item, dependent);
        return;
      case 'updatedependent':
        if (!dependent) throw new Error('Dependent not found');
        await this.userVendorProductsService.updateDependentForUserVendorProduct(item, dependent);
        return;
      case 'deactivatedependent':
        if (!dependent) throw new Error('Dependent not found');
        await this.userVendorProductsService.removeDependentFromUserVendorProduct(item, dependent);
        return;
      case 'deactivateprimary':
        await this.userVendorProductsService.deactivatePrimaryForUserVendorProduct(item);
        return;
      case 'changeplan':
        if (!groupId) throw new Error('groupId required for changeplan');
        await this.userVendorProductsService.changePlanForUserVendorProduct(item, groupId);
        return;
      case 'updatemember':
        await this.userVendorProductsService.updatePrimaryForUserVendorProduct(item);
        return;
      default:
        throw new Error(`Unsupported RxValet operation: ${operation}`);
    }
  }

  private getDependentLabel(dependent: any): string {
    const firstName = (dependent?.firstName || '').toString().trim();
    const lastName = (dependent?.lastName || '').toString().trim();
    const relationship = (dependent?.relationship || '').toString().trim();
    const dob = this.formatDependentDob(dependent?.dob || dependent?.dateOfBirth);
    const name = [firstName, lastName].filter(Boolean).join(' ').trim() || dependent?.id || 'Unnamed dependent';
    const details = [relationship, dob].filter(Boolean).join(' | ');
    const label = details ? `${name} (${details})` : name;
    // MemberAddedit dropdown encodes values as "label:value" and splits on ":".
    return label.replace(/:/g, ' - ');
  }

  private formatDependentDob(value: any): string {
    const raw = (value || '').toString().trim();
    if (!raw) {
      return '';
    }
    if (raw.includes('T')) {
      return raw.split('T')[0];
    }
    return raw;
  }

  private getVendorUpdateErrorMessage(error: any, operation?: string): string {
    return (
      error?.data?.error ||
      error?.data?.message ||
      error?.message ||
      `Unable to process RxValet operation${operation ? ` (${operation})` : ''}.`
    );
  }

  private getUserVendorProductEditFields(): Field[] {
    return [
      { name: 'modifiedAt', label: 'Updated', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'userName', label: 'User Name', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'vendorName', label: 'Product Name', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'vendorSession', label: 'Vendor Session', fieldType: FieldType.F_STRING },
      { name: 'wallitSession', label: 'Wallit Session', fieldType: FieldType.F_STRING },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
    ];
  }

  private getUserVendorProductEditData(item: any): Record<string, any> {
    return {
      modifiedAt: item?.modifiedAt || '',
      userName: item?.userName || '',
      vendorName: item?.vendorName || '',
      vendorSession: item?.vendorSession || '',
      wallitSession: item?.wallitSession || '',
      metadata: item?.metadata || {},
    };
  }
}
